import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type {
  CalendarSlotsRequest,
  CalendarSlotsResponse,
} from '@/types/scheduling-api';

/**
 * Hits POST /api/v1/Practice/calendarSlots — the Flutter calendar grid feed.
 * Returns provider-organized slot data, NOT a flat appointment list. Use
 * `mapCalendarSlotsToAppointments` to flatten for grid components.
 *
 * Mirrors Flutter's CalendarSlotsEvent → SchedulingDataSourceImpl.calendarSlots().
 */

interface CalendarSlotsParams {
  organizationId: string;
  practiceId: string;
  /** ISO date or full datetime; the hook ensures the day-end behavior. */
  startDate: string;
  endDate: string;
  practitioners?: string[];
  searchText?: string;
  pageNumber?: number;
  pageSize?: number;
}

/**
 * Flutter's datasource bumps endDate to 23:59:59 before sending. We mirror
 * that contract: callers can pass `YYYY-MM-DD` or a full ISO and we'll
 * normalize to a full-day range.
 */
function toDayBoundaryISO(input: string, end: boolean): string {
  // Already a full datetime — pass through.
  if (input.includes('T')) return input;
  return end ? `${input}T23:59:59Z` : `${input}T00:00:00Z`;
}

/**
 * NO CACHE — Andrew, 2026-09-21: "don't cache the calendar slots at all"
 * (PROD-2444).
 *
 * The app-wide default is `staleTime: 5 * 60_000` with `refetchOnWindowFocus`
 * and `refetchOnReconnect` both OFF, which is right for a service catalogue
 * and wrong for this: a slot feed answers *what is free right now*, and two
 * operators booking the same machine minutes apart is the case a five-minute
 * memory loses. `refetchOnMount: 'always'` alone only covers a fresh mount.
 *
 *   staleTime 0            — never reuse an answer without asking again
 *   gcTime 0               — drop it the moment nothing is watching, so a
 *                            remount shows a loading state rather than
 *                            painting yesterday's windows and correcting
 *                            itself a beat later
 *   refetchOnMount 'always'
 *   refetchOnWindowFocus / refetchOnReconnect — re-read after the operator
 *                            comes back from another tab or a dropped link,
 *                            overriding the global opt-outs on purpose
 *
 * `gcTime: 0` is safe for the consumers that gate with `enabled` rather than
 * by unmounting (the Devices tab badge, PROD-2364): a disabled query still has
 * a mounted observer, so nothing is collected while the page is on screen.
 */
const NO_CACHE = {
  staleTime: 0,
  gcTime: 0,
  refetchOnMount: 'always',
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
} as const;

export function useCalendarSlots(params: CalendarSlotsParams | null) {
  return useQuery({
    queryKey: ['calendar-slots', params],
    queryFn: async ({ signal }) => {
      if (!params) throw new Error('useCalendarSlots called without params');
      const body: CalendarSlotsRequest = {
        organizationId: params.organizationId,
        practiceId: params.practiceId,
        startDate: toDayBoundaryISO(params.startDate, false),
        endDate: toDayBoundaryISO(params.endDate, true),
        practitioners: params.practitioners ?? [],
        searchText: params.searchText ?? '',
        pageNumber: params.pageNumber ?? 0,
        pageSize: params.pageSize ?? 5,
      };
      const res = await post<CalendarSlotsResponse>('/api/v1/Practice/calendarSlots', body, { signal });
      // A practice with no slots returns an empty/null body. React Query
      // rejects an `undefined` return ("data cannot be undefined") which
      // would fire the global error toast — coalesce to [] (the response is
      // ProviderWithSlots[], and the mappers already handle empty).
      return res ?? [];
    },
    enabled: !!params?.practiceId,
    ...NO_CACHE,
  });
}
